import type { ReactElement } from "react";

import ContentItem from "@/components_v2/ContentElements/ContentItem";
import SliderDropdownElement from "@/components_v2/ContentElements/SliderDropdownElement";
import { TagsElement } from "@/components_v2/ContentElements/TagsElement";
import TextElement from "@/components_v2/ContentElements/TextElement";
import { TextType } from "@/components_v2/ContentElements/types";
import { OptionType } from "@/shared/utils/constants";
import {
  AnswerResponse,
  AnswerTextAnswer,
  ChoiceWithScaleOption,
  OptionResponse,
  QuestionResponse,
} from "@/redux/survey/types";
import type { SliderDisplayValue } from "../types";

const normalizeTextAnswer = (
  textAnswer: AnswerTextAnswer | null | undefined,
): AnswerTextAnswer => {
  if (textAnswer == null) {
    return "";
  }

  return textAnswer;
};

const isObjectChoiceArray = (
  value: AnswerTextAnswer,
): value is ChoiceWithScaleOption[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  typeof value[0] === "object" &&
  value[0] != null &&
  "choice" in value[0];

const buildAnswerMap = (
  answers: AnswerResponse[],
): Record<number, AnswerResponse> =>
  answers.reduce<Record<number, AnswerResponse>>((acc, answer) => {
    acc[answer.option] = {
      ...answer,
      textAnswer: normalizeTextAnswer(answer.textAnswer),
    };
    return acc;
  }, {});

const getScaleLabel = (
  option: OptionResponse,
  scale: number | string | undefined,
): string => {
  if (scale == null || scale === "") {
    return "";
  }

  const match = option.relatedOptions.find(
    (related) => related.id === Number(scale),
  );

  return match?.label ?? String(scale);
};

const getTextDisplayValue = (
  textAnswer: AnswerTextAnswer | undefined,
): string => {
  if (textAnswer == null || textAnswer === "") {
    return "";
  }

  if (typeof textAnswer === "string") {
    return textAnswer;
  }

  if (isObjectChoiceArray(textAnswer)) {
    return "";
  }

  return textAnswer
    .filter((item): item is string => typeof item === "string")
    .join(", ");
};

const getScaleFromAnswer = (
  textAnswer: AnswerTextAnswer | undefined,
): number | string | undefined => {
  if (textAnswer == null || textAnswer === "") {
    return undefined;
  }

  if (typeof textAnswer === "string") {
    return textAnswer;
  }

  if (isObjectChoiceArray(textAnswer)) {
    return undefined;
  }

  const first = textAnswer[0];
  return typeof first === "string" || typeof first === "number"
    ? first
    : undefined;
};

const wrapContentItem = (
  key: string,
  content: ReactElement | null,
  label?: string,
): ReactElement | null => {
  if (!content) {
    return null;
  }

  return (
    <ContentItem
      key={key}
      label={label}
      content={content}
      layout="vertical"
      showDivider={false}
    />
  );
};

const buildTextAndNumberItems = (
  question: QuestionResponse,
  answerMap: Record<number, AnswerResponse>,
): ReactElement[] => {
  const textOptions = question.options
    .filter((option) =>
      [OptionType.TEXT.value, OptionType.NUMBER.value].includes(option.type),
    )
    .sort((a, b) => a.id - b.id);

  if (textOptions.length === 0) {
    return [];
  }

  const showOptionLabel = textOptions.length > 1;

  return textOptions
    .map((option) => {
      const value = getTextDisplayValue(answerMap[option.id]?.textAnswer);

      if (!value) {
        return null;
      }

      return wrapContentItem(
        `text-${option.id}`,
        <TextElement value={value} type={TextType.REGULAR} />,
        showOptionLabel ? option.label : undefined,
      );
    })
    .filter((item): item is ReactElement => item != null);
};

const buildSliderValues = (
  question: QuestionResponse,
  answerMap: Record<number, AnswerResponse>,
  otherOption: OptionResponse | undefined,
): SliderDisplayValue[] => {
  const sliderValues: SliderDisplayValue[] = [];

  Object.keys(answerMap)
    .filter((id) => id !== otherOption?.id.toString())
    .forEach((optionId) => {
      const numericId = Number(optionId);
      const option = question.options.find((opt) => opt.id === numericId);
      const answer = answerMap[numericId];

      if (!option || !answer) {
        return;
      }

      sliderValues.push({
        label: option.label,
        scaleLabel: getScaleLabel(
          option,
          getScaleFromAnswer(answer.textAnswer),
        ),
      });
    });

  const otherAnswer = otherOption ? answerMap[otherOption.id] : undefined;

  if (
    otherOption &&
    otherAnswer &&
    isObjectChoiceArray(otherAnswer.textAnswer)
  ) {
    otherAnswer.textAnswer.forEach((choiceItem) => {
      sliderValues.push({
        label: choiceItem.choice,
        scaleLabel: getScaleLabel(otherOption, choiceItem.scale),
      });
    });
  }

  return sliderValues;
};

const buildChoiceLabels = (
  question: QuestionResponse,
  answerMap: Record<number, AnswerResponse>,
  otherOption: OptionResponse | undefined,
  isMultiple: boolean,
): string[] => {
  const selectedOptionIds = Object.keys(answerMap)
    .map(Number)
    .filter((id) => id !== otherOption?.id);

  if (isMultiple) {
    const labels = selectedOptionIds
      .map((id) => question.options.find((option) => option.id === id)?.label)
      .filter((label): label is string => !!label);

    const otherAnswer = otherOption ? answerMap[otherOption.id] : undefined;

    if (otherAnswer && Array.isArray(otherAnswer.textAnswer)) {
      otherAnswer.textAnswer.forEach((item) => {
        if (typeof item === "string") {
          labels.push(item);
        }
      });
    }

    return labels;
  }

  const selectedId = selectedOptionIds[0];
  if (selectedId != null) {
    const label = question.options.find(
      (option) => option.id === selectedId,
    )?.label;
    if (label) {
      return [label];
    }
  }

  const otherAnswer = otherOption ? answerMap[otherOption.id] : undefined;

  if (otherAnswer && Array.isArray(otherAnswer.textAnswer)) {
    const firstString = otherAnswer.textAnswer.find(
      (item): item is string => typeof item === "string",
    );
    if (firstString) {
      return [firstString];
    }
  }

  return [];
};

const buildChoiceContentItem = (
  question: QuestionResponse,
  answerMap: Record<number, AnswerResponse>,
  questionType: string,
  hasSlider: boolean,
  otherOption: OptionResponse | undefined,
): ReactElement | null => {
  const isMultiple = questionType === OptionType.MULTIPLE_CHOICE.value;

  if (hasSlider) {
    const sliderValues = buildSliderValues(question, answerMap, otherOption);

    if (sliderValues.length === 0) {
      return null;
    }

    return wrapContentItem(
      "slider",
      <SliderDropdownElement value={sliderValues} />,
    );
  }

  const labels = buildChoiceLabels(
    question,
    answerMap,
    otherOption,
    isMultiple,
  );

  if (labels.length === 0) {
    return null;
  }

  if (isMultiple) {
    return wrapContentItem(
      "choices",
      <TagsElement value={labels.map((label) => ({ label }))} />,
    );
  }

  return wrapContentItem(
    "choice",
    <TextElement value={labels[0]} type={TextType.REGULAR} />,
  );
};

const getNonPromptAnswers = (
  question: QuestionResponse,
  answers: AnswerResponse[],
): AnswerResponse[] => {
  const promptOptionIds = new Set(
    question.options
      .filter((option) => option.type === OptionType.PROMPT.value)
      .map((option) => option.id),
  );

  return answers.filter((answer) => !promptOptionIds.has(answer.option));
};

export const hasSurveyAnswerContent = (
  question: QuestionResponse,
  answers: AnswerResponse[],
): boolean => {
  const nonPromptOptions = question.options.filter(
    (option) => option.type !== OptionType.PROMPT.value,
  );

  if (nonPromptOptions.length === 0) {
    return false;
  }

  const nonPromptAnswers = getNonPromptAnswers(question, answers);

  if (nonPromptAnswers.length === 0) {
    return false;
  }

  return buildSurveyAnswerContentItems(question, answers).length > 0;
};

export const buildSurveyAnswerContentItems = (
  question: QuestionResponse,
  answers: AnswerResponse[],
): ReactElement[] => {
  const nonPromptOptions = question.options.filter(
    (option) => option.type !== OptionType.PROMPT.value,
  );

  if (nonPromptOptions.length === 0) {
    return [];
  }

  const answerMap = buildAnswerMap(getNonPromptAnswers(question, answers));
  const questionType = nonPromptOptions[0]?.type;
  const hasSlider = nonPromptOptions.some(
    (option) => option.relatedOptions.length > 0,
  );
  const otherOption = question.options.find(
    (option) => option.type === OptionType.CHOICE_TEXT.value,
  );

  const isChoiceOrSlider =
    questionType != null &&
    ([
      OptionType.SINGLE_CHOICE.value,
      OptionType.MULTIPLE_CHOICE.value,
    ].includes(questionType) ||
      hasSlider);

  if (isChoiceOrSlider && questionType) {
    const choiceItem = buildChoiceContentItem(
      question,
      answerMap,
      questionType,
      hasSlider,
      otherOption,
    );

    return choiceItem ? [choiceItem] : [];
  }

  return buildTextAndNumberItems(question, answerMap);
};
